// TOEFL iBT Mock 9 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure ("far more … than I had expected",
//                       passive "be required to", reduced relative clauses,
//                       "how important it is to", "no longer"). `accept`
//                       lists other word orders that are also grammatical
//                       and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal message admitting damage to
//                       shared lab equipment and proposing how to put it right.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       on real-name verification AND name a group it could
//                       harm and say what protection they should get instead.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Why weren't you at the seminar yesterday?",
    prefix: "I",
    answer: ["had", "to", "take", "my", "brother", "to", "the", "doctor", "for", "a", "checkup"],
    suffix: ".",
    distractors: ["taking", "has"],
  },
  {
    id: 2,
    speaker: "Did you like the novel you were assigned?",
    prefix: "Actually,",
    answer: ["it", "was", "far", "more", "enjoyable", "than", "I", "had", "expected"],
    suffix: ".",
    distractors: ["most", "expecting"],
  },
  {
    id: 3,
    speaker: "What does the new building policy require?",
    prefix: "All visitors",
    answer: ["are", "required", "to", "sign", "in", "at", "the", "front", "desk"],
    suffix: ".",
    distractors: ["requiring", "signing"],
  },
  {
    id: 4,
    speaker: "How did you hear about the scholarship?",
    prefix: "A friend",
    answer: ["who", "had", "won", "it", "last", "year", "told", "me", "about", "it"],
    suffix: ".",
    distractors: ["whom", "winning"],
  },
  {
    id: 5,
    speaker: "Should I take the advanced course?",
    prefix: "Only",
    answer: ["if", "you", "have", "already", "completed", "the", "introductory", "one"],
    accept: [["if", "you", "have", "completed", "the", "introductory", "one", "already"]],
    suffix: ".",
    distractors: ["completing", "when"],
  },
  {
    id: 6,
    speaker: "Why is the lab so busy this week?",
    prefix: "Everyone",
    answer: ["is", "rushing", "to", "finish", "their", "experiments", "before", "the", "deadline"],
    suffix: ".",
    distractors: ["rushed", "finishing"],
  },
  {
    id: 7,
    speaker: "What did you learn from your internship?",
    prefix: "Above all,",
    answer: ["I", "learned", "how", "important", "it", "is", "to", "communicate", "clearly"],
    suffix: ".",
    distractors: ["what", "importance"],
  },
  {
    id: 8,
    speaker: "Do you know why the meeting was moved?",
    prefix: "Apparently,",
    answer: ["the", "room", "we", "had", "booked", "was", "needed", "for", "an", "exam"],
    suffix: ".",
    distractors: ["booking", "need"],
  },
  {
    id: 9,
    speaker: "Is it difficult to get a room in the dormitory?",
    prefix: "Not if",
    answer: ["you", "apply", "before", "the", "end", "of", "the", "spring", "semester"],
    suffix: ".",
    distractors: ["applying", "until"],
  },
  {
    id: 10,
    speaker: "Why did the city close the old bridge?",
    prefix: "Engineers discovered",
    answer: ["that", "it", "was", "no", "longer", "safe", "for", "heavy", "vehicles"],
    suffix: ".",
    distractors: ["any", "safety"],
  },
];

export const emailTask = {
  recipient: "Dr. Sato",
  scenario:
    "Yesterday, while working alone in the biology department's shared imaging lab, you accidentally knocked over the fluorescence microscope while adjusting its stage. The lens housing cracked and the microscope no longer focuses properly. Several graduate students have booked the microscope for experiments this week, and you know the department's equipment budget is limited. Write an email to Dr. Sato, the lab supervisor.",
  points: [
    "Explain clearly what happened and take responsibility for the damage.",
    "Describe the effects the damage may have on other users of the lab.",
    "Propose concrete steps to repair the situation and prevent similar accidents.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["accident", "accidentally", "knocked", "dropped", "fell", "damaged", "damage", "broke", "broken", "cracked", "lens", "microscope", "stage", "adjusting", "yesterday", "responsibility", "responsible", "my fault", "apologize", "apologise", "sorry", "regret"],
    ["graduate students", "students", "booked", "booking", "bookings", "experiments", "schedule", "delay", "delayed", "research", "deadline", "others", "users", "unavailable", "cannot use", "can't use", "affect", "affected", "impact", "inconvenience", "budget"],
    ["repair", "replace", "replacement", "fix", "technician", "service", "pay", "cover", "contribute", "cost", "borrow", "another lab", "alternative", "reschedule", "propose", "suggest", "offer", "training", "procedure", "checklist", "sign", "prevent", "in the future", "future", "supervision", "two people"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "sociology",
  professor: "Dr. Abernathy",
  question:
    "Some governments are considering laws that would require social media platforms to verify every user's real identity before they can post. Supporters argue that this would reduce online harassment and the spread of false information, because people would be accountable for what they say. Should social media platforms be required to verify users' real identities? In your post, also identify a group of people who could be harmed by such a requirement, and explain what protection they should receive instead.",
  posts: [
    {
      name: "Rafael",
      text: "I support verification. Many people write cruel or false things online only because they think no one will ever know who they are. If accounts were linked to real names, most of them would think twice, and it would be much easier to stop organized networks of fake accounts.",
    },
    {
      name: "Lena",
      text: "I'm against it. Anonymity isn't only used by trolls. It also allows people to discuss sensitive topics like health problems or workplace abuse without fear. And collecting everyone's ID creates a huge database that could be hacked or misused.",
    },
  ],
  topicKeywords: [
    "social media", "platform", "platforms", "verify", "verification", "verified", "identity", "identities", "real name", "real names",
    "anonymous", "anonymity", "account", "accounts", "harassment", "bullying", "trolls", "false information", "misinformation", "fake",
    "accountable", "accountability", "privacy", "data", "hacked", "law", "laws", "government", "governments", "freedom", "speech", "users",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on mandatory identity verification",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "i agree", "i disagree", "should be required", "should not be required", "shouldn't be required", "should verify", "support", "oppose", "in favor", "against"],
    },
    {
      label: "Identify a group that could be harmed and say what protection they should receive instead",
      keywords: ["harmed", "harm", "at risk", "danger", "dangerous", "vulnerable", "journalists", "activists", "whistleblowers", "victims", "abuse", "domestic", "minorities", "lgbt", "dissidents", "patients", "teenagers", "instead", "protection", "protect", "pseudonym", "pseudonyms", "exemption", "exempt", "encrypted", "encryption", "privacy", "confidential", "anonymous option", "report", "moderation"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
